import { useMemo, useState } from "react";
import { Box, Button, Chip, IconButton } from "@mui/material";

import { CellContext } from "@tanstack/react-table";
import { Trash2Icon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { DataTable } from "components/DataTable/DataTable";
import { useNavigate } from "hooks/useNavigate";
import { INotificationHandler } from "interfaces/notification.interface";
import { IZone } from "interfaces/zone.interface";

import { DeleteHandlerDialog } from "./dialogs/DeleteHandlerDialog";
import { NotificationHandlerDialog } from "./dialogs/NotificationHandlerDialog";
import { ToggleHandlerDialog } from "./dialogs/ToggleHandlerDialog";

import { NotificationEnableDisableButton } from "../../../../components/NotificationEnableDisableButton";
import {
  InheritedChip,
  MemoizedZoneComponentComponent,
} from "../../rules/components/RulesTableRowComponents";

function NotificationType(
  cell: CellContext<INotificationHandler, INotificationHandler["type"]>,
) {
  const { t } = useTranslation();

  const type = cell.getValue();

  return <Chip label={t(type)} />;
}

type NotificationHandlerListProps = {
  zoneId: string;
  zoneEntity: Record<string, IZone>;
  notificationHandlers: INotificationHandler[];
  hasHandlerUpdatePermission: boolean;
};

export function NotificationHandlerList({
  zoneId,
  zoneEntity,
  notificationHandlers,
  hasHandlerUpdatePermission,
}: NotificationHandlerListProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [showInfo, setShowInfo] = useState<string | null>(null);
  const [showToggle, setShowToggle] = useState<string | null>(null);
  const [showDelete, setShowDelete] = useState<string | null>(null);

  const columns = useMemo(
    () => [
      {
        accessorKey: "type",
        header: t("Type"),
        cell: NotificationType,
      },
      {
        accessorKey: "description",
        header: t("Description"),
      },
      {
        accessorKey: "inherited",
        header: t("inherited_direct"),
        cell: InheritedChip,
      },
      {
        accessorKey: "zone_id",
        header: t("inherited_from"),
        cell: ({ row }: CellContext<INotificationHandler, unknown>) => {
          const zone = zoneEntity[row.original.zone_id ?? ""];

          if (!zone) return null;

          return (
            <MemoizedZoneComponentComponent
              zone={zone}
              selectedZone={zoneId}
              hasPermission={zone.hasAccess}
              onClick={(zone) => navigate(`/administration/zones/${zone.id}`)}
              ruleEntityType={"zone"}
            />
          );
        },
      },
      {
        accessorKey: "enabled",
        header: t("enabled"),
        cell: ({ row }: CellContext<INotificationHandler, unknown>) => {
          const { inherited, enabled, id } = row.original;

          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <NotificationEnableDisableButton
                enabled={enabled ?? false}
                inherited={inherited ?? false}
                type="handler"
                hasNoPermission={!hasHandlerUpdatePermission}
                callback={() => setShowToggle(id)}
              />
              <Button variant={"text"} onClick={() => setShowInfo(id)}>
                {t("details")}
              </Button>
              {inherited || !hasHandlerUpdatePermission ? (
                <Box sx={{ visibility: "hidden", width: "32px" }} />
              ) : (
                <IconButton size={"small"} onClick={() => setShowDelete(id)}>
                  <Trash2Icon />
                </IconButton>
              )}
            </Box>
          );
        },
      },
    ],
    [hasHandlerUpdatePermission, navigate, t, zoneEntity, zoneId],
  );

  const infoHandler = notificationHandlers.find((h) => h.id === showInfo);
  const toggleHandler = notificationHandlers.find((h) => h.id === showToggle);

  return (
    <>
      <DataTable
        tableId={"notification-handler-list"}
        columns={columns}
        data={notificationHandlers}
        getRowId={(row) => row.id}
      />
      <NotificationHandlerDialog
        key={infoHandler?.id}
        isOpen={!!infoHandler}
        defaultValues={infoHandler}
        onClose={() => setShowInfo(null)}
        zoneId={zoneId}
      />
      <ToggleHandlerDialog
        handler={toggleHandler}
        onClose={() => setShowToggle(null)}
      />
      <DeleteHandlerDialog
        handlerId={showDelete}
        onClose={() => setShowDelete(null)}
      />
    </>
  );
}
